import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useRouteNavigation, { formatDuration, formatDistance } from './useRouteNavigation';
import useCurrentPosition from './useCurrentPosition';
import NavigationPanel from './NavigationPanel';
import TurnCard from './TurnCard';
import { SERVICE_STATUSES } from '../../../constants';
import { motion, AnimatePresence } from 'framer-motion';

function MapAutoFit({ points, padding }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 0) {
      const validPoints = points.filter(p => p && typeof p[0] === 'number' && typeof p[1] === 'number');
      if (validPoints.length > 0) {
        const bounds = L.latLngBounds(validPoints);
        map.fitBounds(bounds, { padding: padding || [50, 50], maxZoom: 16 });
      }
    }
  }, [points, map, padding]);
  return null;
}

function MapCenterUpdater({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position && position.lat && position.lng) {
      map.panTo([position.lat, position.lng], { animate: true, duration: 0.5 });
    }
  }, [position, map]);
  return null;
}

const driverSvg = (heading) => `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="${typeof heading === 'number' ? `transform: rotate(${heading}deg)` : ''}">
  <circle cx="12" cy="12" r="11" fill="var(--bry-accent)" fill-opacity="0.25"/>
  <circle cx="12" cy="12" r="7" fill="var(--bry-accent)" stroke="white" stroke-width="2"/>
  <path d="M12 4L14 10H10L12 4Z" fill="white"/>
</svg>`;

const createIcon = (svgHtml) => L.divIcon({
  html: svgHtml,
  className: 'custom-nav-icon',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

const pickupIcon = L.divIcon({
  html: `<div style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:var(--bry-accent);border-radius:50%;color:white;font-weight:bold;font-size:14px;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">O</div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const dropoffIcon = L.divIcon({
  html: `<div style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:var(--bry-bg-secondary);border-radius:50%;color:white;font-weight:bold;font-size:14px;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">D</div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

export default function NavigationView({ 
  service, 
  isDriver = true, 
  onClose,
  onStatusChange,
  updating = false,
  confirmationToken = null,
  onOpenChat,
  onCancel,
}) {
  const pickup = service?.pickup;
  const dropoff = service?.dropoff;
  const status = service?.status;

  const { position: gpsPosition } = useCurrentPosition({ enabled: isDriver });
  const driverPos = gpsPosition;

  const navEnabled = !!(pickup && dropoff);
  const {
    route,
    loading,
    currentStep,
    currentStepIndex,
    remainingDistance,
    remainingDuration,
    totalDistance,
    totalDuration,
  } = useRouteNavigation({
    pickup,
    dropoff,
    driverPos: status === SERVICE_STATUSES.DRIVER_ASSIGNED || status === SERVICE_STATUSES.IN_PROGRESS ? driverPos : null,
    status,
    enabled: navEnabled,
  });

  const mapCenter = useMemo(() => {
    if (driverPos) return [driverPos.lat, driverPos.lng];
    if (pickup) return [pickup.lat, pickup.lng];
    return [8.8872, -64.2444];
  }, [driverPos, pickup]);

  const fitPoints = useMemo(() => {
    const pts = [];
    if (pickup) pts.push([pickup.lat, pickup.lng]);
    if (dropoff) pts.push([dropoff.lat, dropoff.lng]);
    if (driverPos) pts.push([driverPos.lat, driverPos.lng]);
    return pts;
  }, [pickup, dropoff, driverPos]);

  const driverIcon = useMemo(() => createIcon(driverSvg(driverPos?.heading)), [driverPos?.heading]);

  return (
    <motion.div
      className="navigation-view bry-bottom-sheet"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        background: 'var(--bry-bg-primary)',
      }}
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
    >
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {navEnabled && (
          <MapContainer
            center={mapCenter}
            zoom={15}
            style={{ width: '100%', height: '100%' }}
            zoomControl={false}
            attributionControl={false}
          >
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
              maxZoom={20}
              subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
            />

            {route?.coordinates && route.coordinates.length > 1 && (
              <Polyline
                positions={route.coordinates}
                color="var(--bry-accent)"
                weight={6}
                opacity={0.9}
                lineJoin="round"
              />
            )}

            {pickup && (
              <Marker position={[pickup.lat, pickup.lng]} icon={pickupIcon} />
            )}

            {dropoff && (
              <Marker position={[dropoff.lat, dropoff.lng]} icon={dropoffIcon} />
            )}

            {driverPos && (
              <Marker position={[driverPos.lat, driverPos.lng]} icon={driverIcon} />
            )}

            <MapAutoFit points={fitPoints} padding={[60, 60]} />
            {isDriver && driverPos && <MapCenterUpdater position={driverPos} />}
          </MapContainer>
        )}

        {/* Top Floating Maneuver HUD (Figma Driving_01_NextStop.png) */}
        {currentStep && (
          <div className="absolute top-4 left-4 right-4 z-[1000] pointer-events-none">
            <div className="pointer-events-auto max-w-md mx-auto">
              <TurnCard
                type={currentStep.modifier || currentStep.type}
                instruction={currentStep.action || currentStep.name || 'Sigue la ruta en el mapa'}
                distance={currentStep.distance ? formatDistance(currentStep.distance) : 'En ruta'}
              />
            </div>
          </div>
        )}

        {!navEnabled && (
          <div className="bry-empty-state d-flex align-items-center justify-content-center h-100 text-[var(--bry-text-primary)]">
            <p>No hay información de ruta disponible</p>
          </div>
        )}

        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle text-[var(--bry-text-primary)] bg-[var(--bry-bg-card)] p-4 rounded-xl shadow-lg flex flex-col items-center"
            style={{ zIndex: 1001 }}
          >
            <div className="spinner-border text-[var(--bry-accent)]" role="status" />
            <div className="mt-3 font-semibold">Calculando ruta...</div>
          </div>
        )}

        <NavigationPanel
          currentStep={currentStep}
          currentStepIndex={currentStepIndex}
          steps={route?.steps || []}
          remainingDistance={remainingDistance}
          remainingDuration={remainingDuration}
          formatDistance={formatDistance}
          formatDuration={formatDuration}
          service={service}
          isDriver={isDriver}
          onClose={onClose}
          onStatusChange={onStatusChange}
          updating={updating}
          confirmationToken={confirmationToken}
          onOpenChat={onOpenChat}
          onCancel={onCancel}
        />
      </div>
    </motion.div>
  );
}

